// Detector de versión nueva (#1451): que nadie tenga que borrar el caché
// después de un deploy.
//
// Por qué existe (26-sep-2026): el service worker ya es suicida desde el 15-ago
// y `index.html` va con `no-cache`, así que eso ya no es la causa. Lo que queda
// es la pestaña que se abrió ANTES del deploy (en los PCs del hospital, días
// enteros): sigue corriendo el bundle viejo en memoria, nunca se entera de la
// versión nueva, y cuando pide un chunk del deploy anterior Cloudflare Pages ya
// no lo tiene: hasta #1717 respondía el index.html del fallback SPA (200), y desde
// el 18-oct-2026 un 404 no-store (public/static/404.html). Igual el chunk no carga
// y esa parte de la app queda rota hasta que alguien recarga, que es lo que el
// médico llama "borrar el caché".
//
// Qué hace:
//   R1 · compara el `main.<hash>.js` de su propio <script> con el que declara
//        `/index.html` pedido sin caché (al volver visible, al recuperar el foco
//        y cada 5 min). En dev/jest no hay `main.<hash>.js`: queda inerte.
//   R2 · DESCARTADO (v3, 26-sep-2026, Sebastián: "No recargar sola nunca").
//        Recargar sola una pestaña oculta perdía trabajo que no nace de una
//        tecla (transcripción del Transcriptor sin guardar, job de IA en
//        curso). La app NUNCA se recarga sola por versión nueva.
//   R3 · avisa a la barra (`components/VersionNuevaBar.jsx`): el botón
//        "Actualizar" es la ÚNICA vía de recarga por versión nueva.
//        La × la esconde hasta la próxima versión detectada.
//   R4 · si un chunk falla al cargar, guarda y recarga UNA vez; no vuelve a
//        recargar por eso antes de 60 s (marca en sessionStorage). v2: sin
//        conexión no recarga (dejaba al médico en "sin conexión") y reintenta
//        al volver el evento `online`.
//
// Todo es fail-silent: un problema acá no puede tumbar la app.

import { guardarLoPendiente } from "./sw-reload-guard";

const INTERVALO_MS = 5 * 60 * 1000;
// Misma ventana que da el service worker suicida: alcanza para que salgan los
// PATCH de las celdas sucias y el flush de telemetría (fetch keepalive).
const GRACIA_MS = 800;
// Anti-loop de R4.
const R4_KEY = "smartdx.chunk_reload.v1";
const R4_VENTANA_MS = 60 * 1000;

const RE_MAIN = /\/static\/js\/main\.([a-z0-9]+)\.js/;

// Indirección para poder espiar la recarga en los tests (jsdom no deja
// redefinir `window.location.reload`).
export const _nav = {
  reload: () => window.location.reload(),
};

let estado = null; // { actual, nueva } cuando hay versión nueva
let descartada = null; // hash nuevo cuya barra el médico cerró con la ×
const suscriptores = new Set();
let enVuelo = null;
let recargando = false;
let esperandoRed = null; // R4: { err, filename, graceMs } a reintentar en `online`

function notificar() {
  for (const fn of suscriptores) {
    try {
      fn(estado);
    } catch (_) {}
  }
}

/** Versión nueva detectada, o `null`. */
export function getVersionNueva() {
  return estado;
}

/** R3: hash cuya barra se cerró con la ×, o `null`. */
export function getVersionDescartada() {
  return descartada;
}

/** R3 · la ×: esconde la barra hasta que se detecte OTRA versión. No recarga. */
export function descartarVersionNueva() {
  if (!estado) return;
  descartada = estado.nueva;
  notificar();
}

/**
 * Se suscribe a los cambios de "versión nueva disponible". Devuelve la función
 * para desuscribirse.
 */
export function suscribirVersionNueva(fn) {
  if (typeof fn !== "function") return () => {};
  suscriptores.add(fn);
  return () => suscriptores.delete(fn);
}

/** Saca el hash de `main.<hash>.js` de un texto, o `null`. */
export function hashDeTexto(texto) {
  if (typeof texto !== "string") return null;
  const m = texto.match(RE_MAIN);
  return m ? m[1] : null;
}

/** Hash del bundle que corre esta pestaña (su propio <script>), o `null`. */
export function hashPropio(doc = typeof document !== "undefined" ? document : null) {
  try {
    if (!doc) return null;
    const scripts = doc.querySelectorAll("script[src]");
    for (const s of scripts) {
      const h = hashDeTexto(s.getAttribute("src") || "");
      if (h) return h;
    }
  } catch (_) {}
  return null;
}

async function hashServido(fetchImpl) {
  try {
    const base = process.env.PUBLIC_URL || "";
    const res = await fetchImpl(`${base}/index.html`, {
      cache: "no-store",
      credentials: "same-origin",
    });
    if (!res || !res.ok) return null;
    return hashDeTexto(await res.text());
  } catch (_) {
    return null;
  }
}

function esperar(ms) {
  return ms > 0 ? new Promise((r) => setTimeout(r, ms)) : Promise.resolve();
}

function leerMarca(key) {
  // Lanza si sessionStorage no está disponible: el que llama decide.
  const raw = window.sessionStorage.getItem(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}

function escribirMarca(key, valor) {
  window.sessionStorage.setItem(key, JSON.stringify(valor));
}

/**
 * Guarda lo pendiente y recarga. Solo lo llaman el botón "Actualizar" (R3) y
 * la curación de un chunk viejo (R4).
 */
async function recargar({ meta, graceMs = GRACIA_MS }) {
  if (recargando) return false;
  recargando = true;
  try {
    await guardarLoPendiente({ type: "version_reload", meta });
    await esperar(graceMs);
    _nav.reload();
    return true;
  } catch (_) {
    return false;
  } finally {
    recargando = false;
  }
}

/**
 * R1: compara el hash propio con el servido. Marca solo si los dos existen y
 * son distintos. Nunca lanza. Solo marca: no recarga (R2 descartado).
 */
export async function comprobarVersion({
  fetchImpl = typeof fetch !== "undefined" ? fetch : null,
} = {}) {
  try {
    const actual = hashPropio();
    if (!actual || typeof fetchImpl !== "function") return false;
    if (!enVuelo) {
      enVuelo = hashServido(fetchImpl).finally(() => {
        enVuelo = null;
      });
    }
    const nueva = await enVuelo;
    if (!nueva || nueva === actual) return false;
    if (!estado || estado.nueva !== nueva) {
      estado = { actual, nueva };
      // Una versión DISTINTA de la que se cerró con la × vuelve a mostrar la barra.
      if (descartada !== nueva) descartada = null;
      notificar();
    }
    return true;
  } catch (_) {
    return false;
  }
}

/** R3: el botón "Actualizar" de la barra. Guarda y recarga aunque esté visible. */
export function actualizarAhora({ graceMs = GRACIA_MS } = {}) {
  const meta = { reason: "version", action: "boton" };
  if (estado) {
    meta.source = estado.actual;
    meta.target = estado.nueva;
  }
  return recargar({ meta, graceMs });
}

// ── R4 · chunk viejo ─────────────────────────────────────────────────────

const RE_CHUNK = /Loading (CSS )?chunk|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i;
const RE_TOKEN_HTML = /Unexpected token ['"]?</;

function esScriptPropio(filename) {
  if (typeof filename !== "string" || !filename) return false;
  try {
    const url = new URL(filename, window.location.href);
    return url.origin === window.location.origin && /\/static\/js\/[^/]+\.js$/.test(url.pathname);
  } catch (_) {
    return false;
  }
}

/**
 * Qué cuenta como "el chunk que pidió esta pestaña ya no existe".
 * @param {unknown} err error, rechazo o mensaje
 * @param {string} [filename] script donde ocurrió (evento `error` de window)
 */
export function esErrorDeChunk(err, filename) {
  if (!err) return false;
  const nombre = typeof err === "object" ? String(err.name || "") : "";
  const mensaje = typeof err === "string" ? err : String(err.message || "");
  if (nombre === "ChunkLoadError") return true;
  if (RE_CHUNK.test(mensaje)) return true;
  if (
    (nombre === "SyntaxError" || typeof err === "string") &&
    RE_TOKEN_HTML.test(mensaje) &&
    !/JSON/i.test(mensaje) &&
    esScriptPropio(filename)
  ) {
    return true;
  }
  return false;
}

/**
 * R4: si es un chunk viejo, guarda y recarga una sola vez por ventana de 60 s.
 * Nunca lanza.
 */
export async function manejarErrorDeChunk(err, filename, { graceMs = GRACIA_MS } = {}) {
  try {
    if (!esErrorDeChunk(err, filename)) return false;
    // v2: sin red, recargar deja al médico en la página de "sin conexión". Se
    // guarda el caso y se reintenta la regla completa cuando vuelva `online`.
    if (sinConexion()) {
      esperarRed(err, filename, graceMs);
      return false;
    }
    try {
      const previa = leerMarca(R4_KEY);
      if (previa && Date.now() - (previa.at || 0) < R4_VENTANA_MS) return false;
      // La marca va ANTES de recargar: es lo que corta el loop.
      escribirMarca(R4_KEY, { at: Date.now() });
    } catch (_) {
      // Sin sessionStorage no hay cómo frenar un loop: una vista rota es
      // menos mala que una pestaña que se recarga sin parar.
      return false;
    }
    const meta = { reason: "chunk" };
    const actual = hashPropio();
    if (actual) meta.source = actual;
    return await recargar({ meta, graceMs });
  } catch (_) {
    return false;
  }
}

function sinConexion() {
  try {
    return typeof navigator !== "undefined" && navigator.onLine === false;
  } catch (_) {
    return false;
  }
}

function esperarRed(err, filename, graceMs) {
  const yaEsperaba = !!esperandoRed;
  esperandoRed = { err, filename, graceMs };
  if (yaEsperaba) return; // un solo listener, con el último error
  try {
    window.addEventListener(
      "online",
      () => {
        const caso = esperandoRed;
        esperandoRed = null;
        if (caso) manejarErrorDeChunk(caso.err, caso.filename, { graceMs: caso.graceMs });
      },
      { once: true },
    );
  } catch (_) {
    esperandoRed = null;
  }
}

/**
 * Cablea los disparadores. Devuelve la función que los saca (para tests). Se
 * llama una vez al arrancar la app; llamarla de nuevo reemplaza la instalación.
 */
let desinstalarPrevio = null;
export function installVersionNueva({
  fetchImpl,
  graceMs = GRACIA_MS,
  intervalMs = INTERVALO_MS,
} = {}) {
  if (desinstalarPrevio) desinstalarPrevio();
  if (typeof window === "undefined" || typeof document === "undefined") return () => {};
  const opts = {};
  if (fetchImpl) opts.fetchImpl = fetchImpl;

  // Al volver visible se chequea; al ocultarse NO pasa nada (R2 descartado).
  const alCambiarVisibilidad = () => {
    if (document.visibilityState === "visible") comprobarVersion(opts);
  };
  const alFoco = () => comprobarVersion(opts);
  const alError = (ev) => {
    manejarErrorDeChunk(ev?.error || ev?.message, ev?.filename, { graceMs });
  };
  const alRechazo = (ev) => {
    manejarErrorDeChunk(ev?.reason, undefined, { graceMs });
  };

  let intervalo = null;
  try {
    document.addEventListener("visibilitychange", alCambiarVisibilidad);
    window.addEventListener("focus", alFoco);
    window.addEventListener("error", alError);
    window.addEventListener("unhandledrejection", alRechazo);
    intervalo = setInterval(() => comprobarVersion(opts), intervalMs);
  } catch (_) {}

  const desinstalar = () => {
    try {
      document.removeEventListener("visibilitychange", alCambiarVisibilidad);
      window.removeEventListener("focus", alFoco);
      window.removeEventListener("error", alError);
      window.removeEventListener("unhandledrejection", alRechazo);
      if (intervalo) clearInterval(intervalo);
    } catch (_) {}
    if (desinstalarPrevio === desinstalar) desinstalarPrevio = null;
  };
  desinstalarPrevio = desinstalar;
  return desinstalar;
}

/** Solo tests: vuelve el módulo a cero (no toca sessionStorage). */
export function __resetVersionNueva() {
  estado = null;
  descartada = null;
  enVuelo = null;
  recargando = false;
  esperandoRed = null;
  notificar();
}
